/* eslint-disable jsx-a11y/anchor-has-content */
import React from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { SectionHeading } from "components/misc/Headings.js";
import { PrimaryCtaLink, PrimaryButton } from "components/misc/Buttons.js";
import { ReactComponent as ArrowRightIcon } from "images/arrow-right-icon.svg";
import { ReactComponent as LocationIcon } from "feather-icons/dist/icons/map-pin.svg";
import { ReactComponent as ClockIcon } from "feather-icons/dist/icons/clock.svg";
import { ReactComponent as ExternalLinkIcon } from "feather-icons/dist/icons/external-link.svg";
import MindAspirantProject from "images/mind-aspirant-project.jpeg";
import VipsoCardImage from "images/products/vipso/vipso-card.png";
import GroupKittyCardImage from "images/products/group-kitty/group-kitty-card.png";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;

const ThreeColumn = tw.div`flex flex-wrap`;
const Column = tw.div``;
const HeadingColumn = tw(Column)`w-full`;
const CardColumn = tw(Column)`w-full sm:w-1/2 lg:w-1/3 mt-12 px-0 sm:px-4`;

const HeadingInfoContainer = tw.div`text-center max-w-2xl mx-auto`;
const HeadingTitle = tw(SectionHeading)`leading-tight`;
const HeadingDescription = tw.p`text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100 mt-8`;
const PrimaryLink = styled(PrimaryCtaLink)`
  ${tw`inline-flex justify-center items-center mt-8`}
  svg {
    ${tw`ml-2 w-5 h-5`}
  }
`;

const cardStyles = css`
  ${tw`block mx-auto max-w-sm transition duration-300`}
`;
const CardLink = styled(motion.a)`
  ${cardStyles}
`;
const CardStatic = styled(motion.div)`
  ${cardStyles}
`;

const CardImage = styled.div((props) => [
  `background-image: url("${props.imageSrc}");`,
  tw`h-80 bg-cover bg-center rounded relative overflow-hidden`,
]);

const CardHoverOverlay = styled(motion.div)`
  background: linear-gradient(to top, rgba(15, 23, 42, 0.92) 0%, rgba(15, 23, 42, 0.75) 55%, rgba(15, 23, 42, 0.35) 100%);
  ${tw`absolute inset-0 flex flex-col justify-end p-5`}
`;

const OverlayTitle = tw.h5`text-white text-lg font-bold`;
const OverlayTagline = tw.p`text-gray-200 text-sm mt-2 leading-relaxed`;
const OverlayActions = tw.div`mt-4 flex flex-wrap gap-2`;
const OverlayPrimary = tw(PrimaryButton)`text-xs py-2 px-4`;
const OverlaySecondary = styled.button`
  ${tw`inline-flex items-center text-xs font-semibold text-white border border-white border-opacity-50 rounded px-4 py-2 hover:bg-white hover:bg-opacity-10 transition duration-200 cursor-pointer bg-transparent`}
`;

const CardText = tw.div`mt-4`;
const CardHeader = tw.div`flex justify-between items-center`;
const CardType = tw.div`text-primary-500 font-bold text-lg`;
const CardTitle = tw.h5`text-xl mt-4 font-bold`;

const CardMeta = styled.div`
  ${tw`flex flex-row flex-wrap justify-between sm:items-center font-semibold tracking-wide text-gray-600 uppercase text-xs`}
`;

const CardMetaFeature = styled.div`
  ${tw`flex items-center mt-4`}
  svg {
    ${tw`w-5 h-5 mr-1`}
  }
`;

const overlayVariants = {
  rest: { y: "100%", opacity: 0 },
  hover: { y: 0, opacity: 1 },
};

const CardContent = ({ card }) => (
  <>
    <CardImage imageSrc={card.imageSrc}>
      <CardHoverOverlay
        variants={overlayVariants}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
      >
        <OverlayTitle>{card.title}</OverlayTitle>
        {card.description && <OverlayTagline>{card.description}</OverlayTagline>}
        <OverlayActions>
          {card.productLink && (
            <OverlayPrimary as="span">View Product →</OverlayPrimary>
          )}
          {card.externalLink && (
            <OverlaySecondary
              as="button"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(card.externalLink, "_blank", "noopener,noreferrer");
              }}
            >
              Visit Site ↗
            </OverlaySecondary>
          )}
        </OverlayActions>
      </CardHoverOverlay>
    </CardImage>
    <CardText>
      <CardHeader>
        <CardType>{card.type}</CardType>
      </CardHeader>
      <CardTitle>{card.title}</CardTitle>
      {card.description && (
        <HeadingDescription tw="mt-3 text-left text-sm">
          {card.description}
        </HeadingDescription>
      )}
      <CardMeta>
        <CardMetaFeature>
          <ClockIcon /> {card.trendingText}
        </CardMetaFeature>
        <CardMetaFeature>
          <LocationIcon /> {card.locationText}
        </CardMetaFeature>
        {card.externalLink && (
          <CardMetaFeature>
            <a
              href={card.externalLink}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLinkIcon />
            </a>
          </CardMetaFeature>
        )}
      </CardMeta>
    </CardText>
  </>
);

export default () => {
  const cards = [
    {
      imageSrc: MindAspirantProject,
      type: "Game & Education",
      title: "Mind Aspirants",
      trendingText: "8 Months",
      locationText: "Ahmedabad, India",
      description: "A gamified quiz platform where users compete in timed quizzes, earn points, and win prize money.",
      productLink: "/products/mind-aspirant",
      externalLink: "https://mindaspirant.com",
    },
    {
      imageSrc: VipsoCardImage,
      type: "Business",
      title: "VIPSO",
      trendingText: "Live on Play Store",
      locationText: "Gandhinagar, India",
      description: "Accounting and inventory management app for procurement, warehouses, staff, and invoices.",
      productLink: "/products/vipso",
      externalLink: "https://play.google.com/store/apps/details?id=com.spurnest.vipso",
    },
    {
      imageSrc: GroupKittyCardImage,
      type: "Finance & Travel",
      title: "Group Kitty",
      trendingText: "On Play Store & App Store",
      locationText: "Gandhinagar, India",
      description: "Split group travel expenses effortlessly. Track trips, settle up, and keep everyone in sync.",
      productLink: "/products/group-kitty",
      externalLink: "https://groupkitty.spurnest.com",
    },
  ];

  return (
    <Container id="our-products">
      <Content>
        <ThreeColumn>
          <HeadingColumn>
            <HeadingInfoContainer>
              <HeadingTitle>Our Products</HeadingTitle>
              <HeadingDescription>
                Real products we've built — from gamified ed-tech and business apps to group expense splitters.
              </HeadingDescription>
              <PrimaryLink href="/contact-us">
                Get a Quote <ArrowRightIcon />
              </PrimaryLink>
            </HeadingInfoContainer>
          </HeadingColumn>
          {cards.map((card, index) => (
            <CardColumn key={index}>
              {card.productLink ? (
                <CardLink
                  href={card.productLink}
                  initial="rest"
                  whileHover="hover"
                  animate="rest"
                >
                  <CardContent card={card} />
                </CardLink>
              ) : (
                <CardStatic initial="rest" whileHover="hover" animate="rest">
                  <CardContent card={card} />
                </CardStatic>
              )}
            </CardColumn>
          ))}
        </ThreeColumn>
      </Content>
    </Container>
  );
};
